import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useEffect } from 'react';

const CALENDLY_URL = "https://calendly.com/meneora/appel-clarte-de-15-minutes";

const CalendlyButton = () => (
  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
    <Button size="lg" className="text-base md:text-lg">
      Réserver un appel clarté (15 min)
    </Button>
  </a>
);

const Professionnel = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));
    return () => elements.forEach(el => observer.unobserve(el));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>

          {/* SECTION 1 — HERO */}
          <section className="text-center mb-20 reveal-on-scroll">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight">
              Tu sens que ton travail ne te correspond plus ?
            </h1>
            <p className="text-xl text-foreground/80 mb-10">
              Et tu ne sais pas encore quoi faire à la place.
            </p>
            <CalendlyButton />
          </section>

          {/* SECTION 2 — IDENTIFICATION */}
          <section className="mb-20 reveal-on-scroll bg-accent/10 p-8 md:p-12 rounded-lg">
            <div className="space-y-4 text-lg text-foreground/90 text-center leading-relaxed">
              <p>Tu continues…</p>
              <p>Mais quelque chose en toi s'éteint.</p>
              <p>Tu te sens à côté.</p>
              <p>Tu doutes.</p>
              <p>Tu te demandes si tu es à la bonne place.</p>
              <p>Tu n'as plus envie… mais tu n'as pas de plan B.</p>
              <p className="font-medium">Et tu tournes en boucle.</p>
            </div>
          </section>

          {/* SECTION 3 — TRANSITION */}
          <section className="mb-20 text-center reveal-on-scroll">
            <div className="space-y-3 text-2xl md:text-3xl font-light text-primary leading-relaxed">
              <p>Tu n'as pas besoin de tout quitter.</p>
              <p className="font-medium">Tu as besoin d'y voir clair.</p>
            </div>
          </section>

          {/* SECTION 4 — OFFRE */}
          <section className="mb-16 reveal-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-3 text-center leading-tight">
              Parcours de réalignement professionnel et personnel
            </h2>
            <p className="text-center text-base text-foreground/70 mb-8 italic">(finançable CPF)</p>
            <div className="space-y-5 text-lg text-foreground/90 leading-relaxed">
              <p>Tu sens que ton travail ne te correspond plus ? Et tu ne sais pas encore quoi faire à la place.</p>
              <p>Tu continues… mais quelque chose en toi s'éteint.</p>
              <p>Ce parcours de <strong>16 heures</strong> est un espace pour faire le point, comprendre ce qui ne te correspond plus et retrouver une direction plus juste.</p>
              <p>On explore :</p>
              <ul className="space-y-3 ml-4">
                <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>ton parcours</span></li>
                <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>ce qui bloque</span></li>
                <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>ce qui t'appelle</span></li>
                <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>ce qui cherche à évoluer</span></li>
              </ul>
              <p>Ce n'est pas seulement un bilan de compétences.</p>
              <p className="font-medium">C'est une démarche pour remettre du sens et du mouvement dans ta vie professionnelle.</p>
            </div>
          </section>

          {/* SECTION 5 — RÉSULTATS */}
          <section className="mb-16 reveal-on-scroll bg-primary/5 p-8 md:p-12 rounded-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6 text-center">
              Tu repars avec :
            </h2>
            <ul className="space-y-4 max-w-2xl mx-auto text-lg">
              <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>plus de clarté</span></li>
              <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>des pistes concrètes</span></li>
              <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>une meilleure compréhension de toi</span></li>
              <li className="flex gap-3"><Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" /><span>une direction plus alignée</span></li>
            </ul>
          </section>

          {/* SECTION 6 — FINANCEMENT */}
          <section className="mb-20 text-center reveal-on-scroll">
            <p className="text-xl text-foreground/90">
              💡 Cet accompagnement peut être financé via ton <strong>CPF</strong>.
            </p>
          </section>

          {/* SECTION 7 — CTA FINAL */}
          <section className="text-center reveal-on-scroll bg-accent/10 p-10 rounded-lg">
            <p className="text-xl text-foreground/90 mb-8 leading-relaxed">
              Tu peux réserver un premier échange pour voir si c'est juste pour toi.
            </p>
            <CalendlyButton />
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Professionnel;
